import React, { useEffect, useState } from 'react';
import ActionButton from 'components/ActionButton';
import styles from './index.module.scss';
import DataTable from 'components/DataTable';
import { DATE_FORMATS, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import moment from 'moment';
import { tableStyles } from 'css/tableStyles';
import { getSupplierInvoices } from 'services';
import { DateRangePickerWrapper } from 'components/Date/DateRangePicker';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';
import ExportCSVButton from 'components/ExportCSVButton';
import Currency from 'components/Currency/Currency';

const columnsForCSV = [
  {
    label: 'Supplier ID',
    value: 'supplierId',
  },
  {
    label: 'Supplier Name',
    value: 'supplierName',
  },
  {
    label: 'Total Invoiced',
    value: 'supplierTotalPOCost',
  },
];

const columns = [
  {
    name: 'Supplier ID',
    selector: 'supplierId',
    sortable: false,
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.supplierId}
        linkTo={`/suppliers/${row.supplierId}`}
      />
    ),
  },
  {
    name: 'Supplier Name',
    selector: 'supplierName',
    sortable: true,
  },
  {
    name: 'Total Invoiced',
    selector: 'supplierTotalPOCost',
    sortable: true,
    // eslint-disable-next-line react/display-name
    format: (row)=><Currency amount={row['supplierTotalPOCost']}/>,
  },
];

const SupplierInvoiceReport = () => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [filterData, setFilterData] = useState<Record<string, any>[]>([]);
  const [startDate, setStartDate] = useState<string | null>(moment().subtract(1, 'week').toString());
  const [endDate, setEndDate] = useState<string | null>(moment().toString());
  const [searchKey, setSearchKey] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await getSupplierInvoices({
        startDate: formatDate(startDate, DATE_FORMATS.YYYY_MM_DD),
        endDate: formatDate(endDate, DATE_FORMATS.YYYY_MM_DD),
      });
      setData(response);
      setFilterData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const onDateSelectionChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    setStartDate(moment(startDate).toISOString());
    setEndDate(moment(endDate).toISOString());
  };

  const onFilter =(searchText)=>{
    if(searchText){
      const tableData = data.filter(obj=>(
        obj?.supplierName.toLowerCase().includes(searchText.toLowerCase())),
      );
      setFilterData(tableData);
    }else{
      setFilterData(data);
    }
    setSearchKey(searchText);
  };

  const getDataTable = () => {
    if (loading) return <Loading type={'mini'} />;

    return (
      <DataTable
        keyField="supplierId"
        columns={columns}
        data={filterData}
        highlightOnHover={true}
        customStyles={tableStyles}
        title={
          <input
            type={'text'}
            key={'saa'}
            className={'form-control'}
            placeholder={'Search Supplier Name'}
            defaultValue={searchKey}
            onChange={e => onFilter(e.target.value)}
          />
        }
        actions={[
          <ExportCSVButton
            data={data}
            key={`supplier-invoice`}
            fileName={`Supplier_Invoice_Report`}
            columnsForCSV={columnsForCSV}
          />,
        ]}
      />
    );
  };

  return (
    <>
      <div className={styles['header']}>
        <div className={styles['heading']}>Supplier Invoice Report</div>
        <div className={styles['filter-container']}>
          <DateRangePickerWrapper
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDateSelectionChange}
          />
          <ActionButton
            text={'Submit'}
            active={!loading}
            isLoading={loading}
            onClick={fetchReport}
            customClass={'ml-4'}
          />
        </div>
      </div>
      {getDataTable()}
    </>
  );
};

export default SupplierInvoiceReport;